import { Injectable, OnDestroy } from '@angular/core';
import { ApiService } from './api.service';
import { UtilService } from './util.service';
import { UpdateCampaignNameServiceService } from './update-campaign-name.service';
import {
    ApexAxisChartSeries,
    ApexChart,
    ApexDataLabels,
    ApexFill,
    ApexLegend,
    ApexPlotOptions,
    ApexStroke,
    ApexTooltip,
    ApexXAxis,
    ApexYAxis,
} from 'ng-apexcharts';
import { MatTableDataSource } from '@angular/material/table';
import { TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { MappingService } from './mapping.service';
import { BehaviorSubject, Subscription } from 'rxjs';
export type ChartOptions = {
    series: ApexAxisChartSeries;
    chart: ApexChart;
    dataLabels: ApexDataLabels;
    plotOptions: ApexPlotOptions;
    yaxis: ApexYAxis;
    xaxis: ApexXAxis;
    fill: ApexFill;
    tooltip: ApexTooltip;
    stroke: ApexStroke;
    legend: ApexLegend;
};

@Injectable({
    providedIn: 'root',
})
export class CampaignReportServiceService implements OnDestroy {
    isUpdateState: boolean = false;
    isRefreshState: boolean = false;
    isLoadingSpinner: boolean = false;
    reportData: any;
    public chartOptions: Partial<ChartOptions>;
    translateCampaignData: any;
    analyticsData: any;
    singleCampaignData: any;
    isLoadCampaign: boolean = false;
    isCampiagnDataLoad = new BehaviorSubject<boolean>(false);
    isApiLoad: Subscription;
    isLoadReportData = new BehaviorSubject<boolean>(false);
    constructor(
        private _api: ApiService,
        private _util: UtilService,
        private _campaignNameUpSer: UpdateCampaignNameServiceService,
        private _translater: TranslateService,
        private _router: Router,
        private _mapping: MappingService,
    ) {
        this._translater.get('CAMPAIGN').subscribe({
            next: (lres: any) => {
                this.translateCampaignData = lres;
            },
        });
    }
    ngOnDestroy(): void {
        if (this.isApiLoad) {
            this.isApiLoad.unsubscribe();
        }
    }

    doRefresh(type?) {
        if (type) {
            this.isRefreshState = true;
        } else {
            this.isUpdateState = true;
        }
        this._api.getData('refresh-campaign-state').subscribe({
            next: (res: any) => {
                if (res.success) {
                    this._util.showToast(res.message);
                    this.getReport();
                } else {
                    this._util.showToast(res.message, true);
                }
                this.isRefreshState = false;
                this.isUpdateState = false;
            },
            error: (err) => {
                console.log('err', err);
                this.isRefreshState = false;
                this.isUpdateState = false;
            },
            complete() { },
        });
    }

    getReport() {
        this.isLoadingSpinner = true;
        this._api
            .getData(`get-report/${this._campaignNameUpSer.campaignId}`)
            .subscribe({
                next: (res: any) => {
                    if (res.success) {
                        this.reportData = res;
                        this.chartOptions = {
                            series: [
                                {
                                    name: this.translateCampaignData[
                                        'TOTAL_REPLIES'
                                    ],
                                    data: res.graph.reply,
                                    color: '#165DFF',
                                },
                                {
                                    name: this.translateCampaignData[
                                        'TOTAL_CLICKS'
                                    ],
                                    data: res.graph.totClick,
                                    color: '#FB1C7A',
                                },
                                {
                                    name: this.translateCampaignData[
                                        'UNIQE_CLICK'
                                    ],
                                    data: res.graph.click,
                                    color: '#FF71AC',
                                },
                                {
                                    name: this.translateCampaignData[
                                        'TOTAL_OPENS'
                                    ],
                                    data: res.graph.totOpen,
                                    color: '#6A3DEB',
                                },
                                {
                                    name: this.translateCampaignData[
                                        'UINQUE_OPEN'
                                    ],
                                    data: res.graph.open,
                                    color: '#AB8DFF',
                                },
                                {
                                    name: this.translateCampaignData[
                                        'TOTAL_SENT'
                                    ],
                                    data: res.graph.sent,
                                    color: '#FBA81C',
                                },
                            ],
                            chart: {
                                type: 'bar',
                                height: 350,
                                zoom: {
                                    enabled: false,
                                },
                                toolbar: {
                                    show: false,
                                },
                            },
                            plotOptions: {
                                bar: {
                                    horizontal: false,
                                    columnWidth: '75%',
                                },
                            },
                            legend: {
                                show: true,
                                position: 'top',
                                horizontalAlign: 'right',
                                offsetX: 0,
                                offsetY: 15,
                                fontWeight: 'medium',
                                fontSize: '12px',
                            },
                            dataLabels: {
                                enabled: false,
                            },
                            yaxis: {
                                labels: {
                                    show: true,
                                    formatter: function (val) {
                                        return val.toFixed(0);
                                    },
                                },
                            },
                            xaxis: {
                                categories: res.graph.date,
                                labels: {
                                    hideOverlappingLabels: false,
                                    show: true,
                                    trim: false,
                                    rotate: 0,
                                    offsetY: 0,
                                    style: {
                                        fontSize: '12px',
                                        cssClass: 'apexcharts-xaxis-label',
                                    },
                                },
                            },
                            fill: {
                                opacity: 1,
                            },
                            tooltip: {
                                enabled: true,
                                custom: ({ series, seriesIndex, dataPointIndex, w }) => {
                                    const value = series[seriesIndex][dataPointIndex]; // Get the value
                                    const date = w.globals.labels?.[dataPointIndex]; // Get the x-axis label (date)
                                    const seriesName = w.globals.seriesNames[seriesIndex]; // Get the series name
                                    const color = w.globals.colors[seriesIndex]; // Get the corresponding series color
                                    let formattedDate = date;
                                    if (date) {
                                        const dateParts = date.split(" "); // ["13", "Feb"]
                                        const day = parseInt(dateParts[0]); // 13
                                        const month = dateParts[1]; // "Feb"
                                        const fullDate = new Date(`${month} ${day}, ${new Date().getFullYear()}`); // "Feb 13, 2024"
                                        const weekday = fullDate.toLocaleDateString("en-US", { weekday: "short" });
                                        formattedDate = `${date}, ${weekday}`;
                                        console.log({ formattedDate })
                                    }
                                    return `
                                    <div style="
                                      background: white;
                                      border-radius: 6px;
                                      font-size: 12px;
                                      min-width: 120px;">
                                      <div style="color: #0B0D13; font-weight: 400; background-color: #ECEFF1; border-bottom: 1px solid #E3E3E3; padding: 6px 8px;">${formattedDate}</div>
                                      <div style="display: flex; align-items: center; padding: 8px;">
                                        <div style="width: 12px; height: 12px; background: ${color}; border-radius: 50%; margin-right: 8px;"></div>
                                        <div style="color: #0B0D13; font-weight: 400;">${seriesName}: <span style="font-weight: 600;">${value}</span></div>
                                      </div>
                                    </div>
                                  `;
                                },
                            },
                        };

                        this.analyticsData = new MatTableDataSource(
                            res.analytics
                        );
                        this._campaignNameUpSer.campaignName = res.data.name
                            ? res.data.name
                            : '';
                        this._mapping.campaignName = res.data.name;
                        this._campaignNameUpSer.campaignStatus = res.data.status
                            ? res.data.status
                            : '';
                        console.log({ analyticsData: this.analyticsData });
                        this.isLoadReportData.next(true);
                    } else {
                        if (res.is_campaign_not_found == 1) {
                            this._router.navigateByUrl('/campaign/all');
                            this._util.showToast(res.message, true);
                        }
                    }
                    this.isLoadingSpinner = false;
                },
                error: (err) => {
                    console.log('error', err);
                    this.isLoadingSpinner = false;
                },
                complete() { },
            });
    }

    getCampaignInfo(id, page?, type?) {
        console.log({ type });
        if (this.isLoadCampaign) return;
        console.log('call api');
        if (this.isApiLoad) this.isApiLoad.unsubscribe();
        console.log('after unsubscribe');
        this.isLoadCampaign = true;
        this.isApiLoad = this._api
            .getData(`get-campaign-info/${id}`)
            .subscribe({
                next: (res: any) => {
                    if (res.success) {
                        this.singleCampaignData = res.data;
                        this.isCampiagnDataLoad.next(true);
                        const statusLower = res.data.status;
                        const currentPath = window.location.pathname;
                        console.log({ currentPath, statusLower });
                        if (statusLower.toLowerCase() != 'draft' && (type || currentPath.includes('report-analytics'))) {
                            console.log('check inside the condition');
                            this.singleCampaignData.completed_step = 0;
                            console.log({ singleCampignData: this.singleCampaignData });
                        }
                        let routes: any;
                        if (page == 'campaign') {
                            routes =
                                statusLower?.toLowerCase() != 'draft'
                                    ? {
                                        0: '/campaign/all/create-campaign/report-analytics',
                                        1: '/campaign/all/create-campaign/prospecting',
                                        2: '/campaign/all/create-campaign/sequences',
                                        3: '/campaign/all/create-campaign/schedule-options',
                                    }
                                    : {
                                        0: '/campaign/all/create-campaign/prospecting',
                                        1: '/campaign/all/create-campaign/sequences',
                                        2: '/campaign/all/create-campaign/schedule-options',
                                    };

                            if (
                                this.singleCampaignData.completed_step >
                                Object.keys(routes).length - 1
                            ) {
                                const excessSteps =
                                    this.singleCampaignData.completed_step -
                                    (Object.keys(routes).length - 1);
                                this.singleCampaignData.completed_step -= excessSteps;
                            }
                            const route = routes[this.singleCampaignData.completed_step];
                            console.log({ route, completed_step: this.singleCampaignData.completed_step });
                            this._router.navigate([route, id]);
                        }

                        localStorage.setItem(
                            'selectedIndex',
                            this.singleCampaignData.completed_step
                        );
                        localStorage.setItem('status', this.singleCampaignData.status);

                        console.log({
                            singleCampaignData: this.singleCampaignData,
                        });
                    } else {
                        this._util.showToast(res.message, true);
                        if (page == 'campaign') {
                            if (res.is_campaign_not_found == 1) {
                                this._router.navigateByUrl('/campaign/all');
                            }
                        }
                    }
                    this.isLoadCampaign = false;
                },
                error: (err) => {
                    console.log('err', err);
                    this.isLoadCampaign = false;
                },
                complete() { },
            });
    }
}
